/**
 * Hook for managing post reactions with optimistic updates
 * Uses the stats endpoint for efficient fetching
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CommunityWallPost, CommunityWallPostApi, ReactionStats } from '../Model/Engagement/CommunityWall/CommunityWallPost';
import { useRootStore } from '../Store/useRootStore';

type UsePostReactionsResult = {
  reactionsByType: Record<string, number>;
  myReaction: string | null;
  reactionCount: number;
  isLoading: boolean;
  toggleReaction: (reactionType: string) => void;
};

export function usePostReactions(post: CommunityWallPost | undefined): UsePostReactionsResult {
  const { authentication } = useRootStore();

  const [stats, setStats] = useState<ReactionStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const currentAthleteId = authentication.athlete?.id;

  // Fetch reaction stats on mount and when post changes
  useEffect(() => {
    if (!post?.id) return;

    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true);

    CommunityWallPostApi
      .getReactionStats(post.id)
      .then((fetchedStats) => {
        if (!cancelled) {
          setStats(fetchedStats);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [post?.id]);

  const toggleReaction = useCallback(
    (reactionType: string) => {
      if (!post || !currentAthleteId) return;

      // Abort any pending reaction request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const currentMyReaction = stats?.myReaction;
      const isRemoving = currentMyReaction === reactionType;

      // Optimistic update
      setStats((prev) => {
        if (!prev) return prev;

        const newCounts = { ...prev.counts };

        // Decrement old reaction count if had one
        if (currentMyReaction && newCounts[currentMyReaction]) {
          newCounts[currentMyReaction] = Math.max(0, newCounts[currentMyReaction] - 1);
          if (newCounts[currentMyReaction] === 0) {
            delete newCounts[currentMyReaction];
          }
        }

        if (isRemoving) {
          return {
            ...prev,
            totalCount: Math.max(0, prev.totalCount - (currentMyReaction ? 1 : 0)),
            counts: newCounts,
            myReaction: null,
          };
        } else {
          newCounts[reactionType] = (newCounts[reactionType] ?? 0) + 1;
          return {
            ...prev,
            totalCount: prev.totalCount + (currentMyReaction ? 0 : 1),
            counts: newCounts,
            myReaction: reactionType,
          };
        }
      });

      // Make the API call
      const apiCall = isRemoving ? post.removeReaction() : post.setReaction(reactionType);

      apiCall
        .then((newStats) => {
          if (newStats) {
            setStats(newStats);
          }
        })
        .catch((error) => {
          if (error?.name !== 'AbortError' && post?.id) {
            CommunityWallPostApi
              .getReactionStats(post.id)
              .then(setStats)
              .catch(() => undefined);
          }
        });
    },
    [post, currentAthleteId, stats],
  );

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    reactionsByType: stats?.counts ?? {},
    myReaction: stats?.myReaction ?? null,
    reactionCount: stats?.totalCount ?? 0,
    isLoading,
    toggleReaction,
  };
}
